import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getTasks, moveTask, createTask, getUsers, type Milestone, type ProjectMember, type Task, type TaskStatus, type User } from '../../packages/api-client/pm-api';
import { getErrorMessage } from '../../packages/api-client/index';
import { StateView } from '../../packages/ui';
import { TaskFilterBar, matchesTaskFilters, taskFilterParams, useTaskFilters } from './sections/taskFilters';
import { StatusMenu } from './StatusMenu';
import { formatDueDate, isDueDateOverdue } from '../../utils/date';
import { PriorityIcon } from '../../components/taskIcons';
import { ClipboardList, Zap, Ban, Search, CircleCheck, CalendarDays, Square, type LucideIcon } from 'lucide-react';
import { statusColor, hexAlpha, varAlpha } from '../../utils/statusColors';

type GroupBy = 'none' | 'assignee' | 'priority';

const COLUMNS: { key: TaskStatus; label: string; Icon: LucideIcon }[] = [
    { key: 'todo', label: 'Todo', Icon: ClipboardList },
    { key: 'in_progress', label: 'In Progress', Icon: Zap },
    { key: 'blocked', label: 'Blocked', Icon: Ban },
    { key: 'review', label: 'Review', Icon: Search },
    { key: 'done', label: 'Done', Icon: CircleCheck },
];

/**
 * KanbanBoard — the board body (group-by control, columns, swimlanes) without
 * any project chrome. Rendered inline as the "Board" tab inside
 * ProjectDetailPage so the project header + tab bar stay visible (matching the
 * AIPilot inline-vs-route pattern). The old standalone /projects/:id/board
 * route now redirects into that tab; this component owns only the board.
 *
 * Group-by lives in the URL (?group=assignee|priority) so a grouped board is
 * shareable / bookmarkable / survives refresh and Back — preserved from #223.
 * The filters are the List's filters (sections/taskFilters.tsx): one URL state.
 *
 * Every card has a status menu, so a status change never needs a drag (spec
 * §8). Moves are optimistic and roll back, with the reason, when the API fails.
 */
// The board shows one page of the largest size the API serves (pagination.MaxLimit).
const BOARD_LIMIT = '100';

export function KanbanBoard({ projectId, projectColor = 'var(--color-primary)', milestones = [], members = [] }: {
    projectId: string; projectColor?: string; milestones?: Milestone[]; members?: ProjectMember[];
}) {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [total, setTotal] = useState(0);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [dragId, setDragId] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState<string | null>(null);
    const [showAdd, setShowAdd] = useState<string | null>(null);
    const [newTitle, setNewTitle] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const filterState = useTaskFilters(searchParams, setSearchParams);
    const paramsKey = JSON.stringify(taskFilterParams(filterState.filters));
    const rawGroup = searchParams.get('group');
    const groupBy: GroupBy = rawGroup === 'assignee' || rawGroup === 'priority' ? rawGroup : 'none';
    const setGroupBy = (g: GroupBy) => setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (g === 'none') next.delete('group'); else next.set('group', g);
        return next;
    }, { replace: true });
    const addInputRef = useRef<HTMLInputElement>(null);
    // Per task: the id of its latest move request, and its last server-confirmed
    // place. Only the latest request may roll back, and it rolls back to the
    // confirmed place, so a slow failure never undoes a newer move.
    const moveSeq = useRef(0);
    const latestMove = useRef<Record<string, number>>({});
    const confirmed = useRef<Record<string, { status: TaskStatus; position: number }>>({});
    // The card whose status menu should take focus back after it re-mounts in
    // its new column; cleared once that render has committed.
    const refocusId = useRef<string | null>(null);
    useEffect(() => { refocusId.current = null; });

    useEffect(() => {
        if (!projectId) return;
        let live = true;
        setLoading(true);
        setLoadError(null);
        getTasks(projectId, { ...JSON.parse(paramsKey) as Record<string, string>, limit: BOARD_LIMIT })
            .then(r => {
                if (!live) return;
                const rows = r.data ?? [];
                setTasks(rows);
                setTotal(r.meta?.total ?? rows.length);
                confirmed.current = Object.fromEntries(rows.map(t => [t.id, { status: t.status, position: t.position }]));
            })
            .catch(err => { if (live) setLoadError(getErrorMessage(err)); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [projectId, paramsKey, reloadKey]);

    useEffect(() => {
        getUsers(500).then(setUsers).catch(() => {});
    }, []);

    useEffect(() => {
        if (showAdd && addInputRef.current) addInputRef.current.focus();
    }, [showAdd]);

    const byStatus = (status: TaskStatus) =>
        tasks.filter(t => t.status === status).sort((a, b) => a.position - b.position);

    function onDragStart(e: React.DragEvent, taskId: string) {
        setDragId(taskId);
        e.dataTransfer.effectAllowed = 'move';
    }

    // Optimistic: the card moves now; if the latest request for it fails, it
    // goes back to its last confirmed place and the reason is shown.
    async function changeStatus(taskId: string, status: TaskStatus) {
        const before = tasks.find(t => t.id === taskId);
        if (!before || before.status === status) return;
        const seq = ++moveSeq.current;
        latestMove.current[taskId] = seq;
        const newPosition = byStatus(status).length;
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status, position: newPosition } : t));
        try {
            await moveTask(taskId, status, newPosition);
            confirmed.current[taskId] = { status, position: newPosition };
        } catch (err) {
            if (latestMove.current[taskId] !== seq) return; // superseded by a newer move
            const back = confirmed.current[taskId] ?? { status: before.status, position: before.position };
            setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...back } : t));
            toast.error(`Couldn't move "${before.title}": ${getErrorMessage(err)}`);
        }
    }

    async function onDrop(e: React.DragEvent, status: TaskStatus) {
        e.preventDefault();
        setDragOver(null);
        const id = dragId;
        setDragId(null);
        if (id) await changeStatus(id, status);
    }

    async function handleAddTask(status: TaskStatus) {
        if (!projectId || !newTitle.trim()) return;
        let task: Task;
        try {
            task = await createTask(projectId, { title: newTitle, status, priority: 'none' });
        } catch (err) {
            toast.error(`Couldn't create the task: ${getErrorMessage(err)}`); // the typed title stays
            return;
        }
        setNewTitle('');
        setShowAdd(null);
        // Don't show a card the current filters would not return.
        if (!matchesTaskFilters(task, filterState.filters)) {
            toast.success('Created; hidden by current filters');
            return;
        }
        confirmed.current[task.id] = { status: task.status, position: task.position };
        setTasks(prev => [...prev, task]);
        setTotal(n => n + 1);
    }

    const isOverdue = isDueDateOverdue;

    function getSwimlaneGroups(): { key: string; label: string; tasks: Task[] }[] {
        if (groupBy === 'assignee') {
            const userMap = Object.fromEntries(users.map(u => [u.id, `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email]));
            const grouped: Record<string, Task[]> = {};
            tasks.forEach(t => { const k = t.assignee_id ?? '__unassigned__'; (grouped[k] = grouped[k] ?? []).push(t); });
            return Object.entries(grouped).map(([key, ts]) => ({
                key, label: key === '__unassigned__' ? 'Unassigned' : (userMap[key] ?? key.slice(0, 8)), tasks: ts,
            }));
        }
        if (groupBy === 'priority') {
            return ['urgent', 'high', 'medium', 'low', 'none'].map(p => ({
                key: p, label: p.charAt(0).toUpperCase() + p.slice(1),
                tasks: tasks.filter(t => t.priority === p),
            })).filter(g => g.tasks.length > 0);
        }
        return [];
    }

    // A render function, not a nested component: a component defined here would
    // remount on every board render and drop an open status menu.
    function renderCard(task: Task) {
        const assignee = task.assignee_id ? users.find(u => u.id === task.assignee_id) : null;
        const initials = assignee
            ? ((assignee.first_name?.[0] ?? '') + (assignee.last_name?.[0] ?? '') || assignee.email[0]).toUpperCase()
            : null;
        const tooltip = assignee
            ? `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim() || assignee.email
            : '';
        return (
            <div key={task.id}
                className={`kanban-card${dragId === task.id ? ' dragging' : ''}`}
                draggable
                onDragStart={e => onDragStart(e, task.id)}
                onDragEnd={() => setDragId(null)}
                onClick={() => navigate(`/tasks/${task.id}`)}
            >
                <Link to={`/tasks/${task.id}`} className="kanban-card-title" onClick={e => e.stopPropagation()}>{task.title}</Link>
                <StatusMenu label={`Status of ${task.title}`} value={task.status}
                    options={COLUMNS.map(c => ({ value: c.key, label: c.label }))}
                    focusOnMount={refocusId.current === task.id}
                    onChoose={v => { refocusId.current = task.id; changeStatus(task.id, v as TaskStatus); }} />
                <div className="kanban-card-meta">
                    {task.priority !== 'none' && (
                        <span className={`priority-badge priority-${task.priority}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><PriorityIcon priority={task.priority} /> {task.priority}</span>
                    )}
                    {task.tags?.map(tag => (
                        <span key={tag.id} className="tag-chip" style={{ background: hexAlpha(tag.color, 0.19), color: tag.color }}>{tag.name}</span>
                    ))}
                    {task.due_date && (
                        <span className={`kanban-card-due${isOverdue(task.due_date) ? ' overdue' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CalendarDays size={12} aria-hidden="true" /> {formatDueDate(task.due_date)}</span>
                    )}
                    {(task.subtask_count ?? 0) > 0 && (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Square size={12} aria-hidden="true" /> {task.subtask_count}</span>
                    )}
                    {initials && (
                        <span
                            title={tooltip}
                            style={{
                                marginLeft: 'auto',
                                width: 24, height: 24, borderRadius: '50%',
                                background: 'var(--color-primary)', color: 'var(--on-accent)',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 'var(--font-size-xs)', fontWeight: 700, flexShrink: 0,
                            }}
                        >
                            {initials}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    function renderColumn(col: typeof COLUMNS[0], colTasks: Task[], dropKey: string = col.key) {
        return (
            <div key={col.key + dropKey} className="kanban-column" role="region" aria-label={col.label}
                onDragOver={e => { e.preventDefault(); setDragOver(dropKey); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={e => onDrop(e, col.key)}>
                <div className="kanban-col-header">
                    <span style={{ color: statusColor(col.key), display: 'inline-flex', alignItems: 'center', gap: 6 }}><col.Icon size={16} aria-hidden="true" /> {col.label}</span>
                    <span className="kanban-col-count">{colTasks.length}</span>
                </div>
                <div className={`kanban-col-body${dragOver === dropKey ? ' drag-over' : ''}`}>
                    {colTasks.map(task => renderCard(task))}
                    {showAdd === col.key ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <input ref={addInputRef} className="form-input" style={{ fontSize: 'var(--font-size-sm)' }}
                                placeholder="Task title…" value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddTask(col.key); if (e.key === 'Escape') { setShowAdd(null); setNewTitle(''); } }} />
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary btn-sm" onClick={() => handleAddTask(col.key)}>Add</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setShowAdd(null); setNewTitle(''); }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <button className="kanban-add-btn" onClick={() => setShowAdd(col.key)}>+ Add task</button>
                    )}
                </div>
            </div>
        );
    }

    const filterBar = <TaskFilterBar projectId={projectId} state={filterState} milestones={milestones} members={members} />;
    if (loading) return <div>{filterBar}<StateView state="loading" label="Loading board" lines={4} /></div>;
    if (loadError) return (
        <div>{filterBar}<StateView state="error" title="The board couldn't be loaded" message={loadError} onRetry={() => setReloadKey(k => k + 1)} /></div>
    );

    return (
        <div>
            {filterBar}
            {total > tasks.length && (
                <p className="kanban-truncated" role="status">
                    Showing {tasks.length} of {total} {filterState.filtered ? 'matching tasks' : 'top-level tasks'}. Narrow the filters or use the List to see the rest.
                </p>
            )}
            {/* Swimlane group-by control */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontWeight: 500 }}>Group by</span>
                {(['none', 'assignee', 'priority'] as GroupBy[]).map(g => (
                    <button key={g} type="button" aria-pressed={groupBy === g} onClick={() => setGroupBy(g)}
                        style={{ minHeight: 32, padding: '0 12px', borderRadius: 99, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', border: 'none', background: groupBy === g ? projectColor : 'var(--color-surface-2)', color: groupBy === g ? 'var(--on-accent)' : 'var(--color-text-muted)' }}>
                        {g === 'none' ? 'No grouping' : g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                ))}
                <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                    {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Board or Swimlane */}
            {groupBy === 'none' ? (
                <div className="kanban-board">
                    {COLUMNS.map(col => renderColumn(col, byStatus(col.key)))}
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    {/* Column headers */}
                    <div style={{ display: 'grid', gridTemplateColumns: `160px repeat(${COLUMNS.length}, minmax(170px,1fr))`, gap: 8, marginBottom: 8, paddingLeft: 8 }}>
                        <div />
                        {COLUMNS.map(col => (
                            <div key={col.key} style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', color: statusColor(col.key), padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 6 }}><col.Icon size={16} aria-hidden="true" /> {col.label}</div>
                        ))}
                    </div>
                    {/* Swimlane rows */}
                    {getSwimlaneGroups().map(group => (
                        <div key={group.key} style={{ display: 'grid', gridTemplateColumns: `160px repeat(${COLUMNS.length}, minmax(170px,1fr))`, gap: 8, marginBottom: 16, alignItems: 'start' }}>
                            <div style={{ padding: '8px 10px', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{groupBy === 'priority' && <PriorityIcon priority={group.key} />} {group.label}</span>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 400 }}>{group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}</div>
                            </div>
                            {COLUMNS.map(col => {
                                const colTasks = group.tasks.filter(t => t.status === col.key);
                                const dropKey = `${group.key}:${col.key}`;
                                return (
                                    <div key={col.key}
                                        onDragOver={e => { e.preventDefault(); setDragOver(dropKey); }}
                                        onDragLeave={() => setDragOver(null)}
                                        onDrop={e => onDrop(e, col.key)}
                                        style={{ minHeight: 60, borderRadius: 'var(--radius-sm)', padding: 4, background: dragOver === dropKey ? varAlpha('accent', 0.07) : 'var(--color-surface-2)', border: '1px solid var(--color-border)' }}>
                                        {colTasks.map(task => renderCard(task))}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * KanbanPage — the old standalone /projects/:id/board route. It used to render
 * its own project header + a stubby two-item tab bar, which dropped the full
 * project-detail chrome (Overview/Tasks/Analytics/Members/…) and forced a Back
 * click to return. The board now lives as an in-page tab inside
 * ProjectDetailPage, so this route just redirects there, forwarding any
 * ?group= param so deep-links to a grouped board still land correctly.
 */
export function KanbanPage() {
    const { id: projectId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!projectId) return;
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'Board');
        navigate(`/projects/${projectId}?${next.toString()}`, { replace: true });
    }, [projectId, searchParams, navigate]);

    return null;
}
